const _excluded = ["data"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { Page, TableList, Typography, PageEmptyState, PageErrorStateIcon, Button, PageContentHeader, Callout, IconButton } from 'seagull';
import { Plus, TrashLine } from 'seagull/icons';
import orderBy from 'lodash/orderBy';
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import InviteOrEditMemberModal from "../../components/InviteOrEditMemberModal";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { selectors } from "../../store/state-selectors";
import { Intent } from "../../infra/access-control";
import { useOnMount } from "../../hooks/useOnMount";
import { routes } from "../../constants";
import RemoveFromTeamModal from "./components/RemoveFromTeamModal";
import { testPrefix } from "./TeamPage.utils";
import AccountantAccessApprovalModal from "./components/AccountantAccessApprovalModal";
import RemoveAccountantModal from "./components/RemoveAccountantModal";
import styles from "./TeamPage.module.scss";
import { handlePromise } from "../../infra/utils";
import MemberRow from "./MemberRow";
import { UserRole } from "../../models/UserInfoModel";
const title = 'Team';
const headerItems = [{
  id: 'memberName',
  value: 'Member name'
}, {
  id: 'role',
  value: 'Role'
}, {
  id: 'status',
  value: 'Status'
}];
const columnDefs = [{
  width: [350, 400]
},
// Member name
{
  width: 120
},
// Role
{
  width: 220
} // Status
];
const accountantsColumnDefs = columnDefs;
const accountantsHeaderItems = [{
  value: 'Company Name'
}, {
  value: 'Date Added'
}, {
  value: 'Status'
}];
const roleOrder = {
  Owner: 1,
  Admin: 2,
  Member: 3
};
const roleToText = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member'
};
export function TeamPageWithData(props) {
  const {
    members,
    accountants,
    loading,
    refresh
  } = props;
  const userRole = useStore(selectors.user.role);
  const userId = useStore(selectors.user.id);
  const businessId = useStore(selectors.user.businessId);
  const {
    api,
    logBI
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const location = useLocation();
  const history = useHistory();
  const membersList = useMemo(() => {
    return orderBy(members.map(member => ({
      user: member,
      role: roleToText[member.effectiveRole()],
      status: member.isInvited() ? 'Pending' : 'Active'
    })), [m => roleOrder[m.role], 'status'], ['asc', 'asc']);
  }, [members]);
  const {
    invitedAccountants,
    activeAccountants
  } = useMemo(() => {
    const normalized = accountants || [];
    return {
      invitedAccountants: normalized.filter(acc => acc.isInvited()),
      activeAccountants: normalized.filter(acc => acc.isActive())
    };
  }, [accountants]);
  useOnMount(() => {
    var _location$state;
    if ((_location$state = location.state) !== null && _location$state !== void 0 && _location$state.openAccountantAccessRequest && invitedAccountants.length > 0) {
      onOpenAccountantAccessRequest(invitedAccountants[0]);
      history.replace(routes.team);
    }
  });
  const onChangeRole = member => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(InviteOrEditMemberModal, {
          member: member,
          allMembers: members,
          onClose: dismissModal,
          onSuccess: refresh
        });
      }
    });
  };
  const onResendInvitation = async member => {
    if (!businessId) return;
    const {
      success
    } = await handlePromise(api.member.resendInvitation(businessId, member.id()));
    if (!success) {
      return overlayContext.showErrorToast('Failed to resend invitation', 'resend-invitation-failure');
    }
    overlayContext.showSuccessToast("Invitation resent to ".concat(member.fullName()), 'resend-invitation-success');
  };
  const onRemoveFromTeam = member => {
    logBI(12202, {
      id: member.id(),
      email: member.email()
    });
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/React.createElement(RemoveFromTeamModal, _extends({
        member: member,
        allMembers: members,
        onRefresh: refresh
      }, p))
    });
  };
  const onInviteMember = () => {
    overlayContext.showModal({
      disableEnforceFocus: true,
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/React.createElement(InviteOrEditMemberModal, {
          onClose: dismissModal,
          onSuccess: refresh,
          allMembers: members
        });
      }
    });
  };
  const onOpenAccountantAccessRequest = accountant => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/React.createElement(AccountantAccessApprovalModal, {
          accountant: accountant,
          dismissModal: dismissModal,
          refresh: refresh
        });
      }
    });
  };
  const onRemoveAccountAccess = accountant => {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/React.createElement(RemoveAccountantModal, _extends({}, p, {
        accountant: accountant,
        refresh: refresh
      }))
    });
  };
  return /*#__PURE__*/React.createElement(Page, {
    header: "".concat(title, " (").concat(membersList.length, ")"),
    subheader: "Add and manage your team members",
    loading: loading,
    "data-testid": testPrefix + '-screen',
    actions: /*#__PURE__*/React.createElement(Button, {
      intent: Intent.InviteTeamMember,
      prefixIcon: /*#__PURE__*/React.createElement(Plus, null),
      onClick: onInviteMember,
      "data-testid": testPrefix + '-btn-invite-member'
    }, "Invite team member")
  }, invitedAccountants.length > 0 && /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    className: styles.callouts
  }, invitedAccountants.map(acc => /*#__PURE__*/React.createElement(Callout, {
    key: acc.id(),
    withAction: /*#__PURE__*/React.createElement(Button, {
      skin: "primaryOutline",
      intent: Intent.OpenAccountantAccessRequest,
      "data-testid": testPrefix + '-view-acc-request',
      onClick: () => onOpenAccountantAccessRequest(acc)
    }, "View details")
  }, "You have a pending Accountant Access request")))), /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs,
    hasActionColumn: true
  }, /*#__PURE__*/React.createElement(Typography, {
    weight: "normal",
    className: styles.tableTitle
  }, "Team members"), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems,
    "data-testid": "".concat(testPrefix, "-table-header")
  }), /*#__PURE__*/React.createElement("div", null, membersList.map((data, idx) => /*#__PURE__*/React.createElement(MemberRow, {
    key: data.user.id(),
    data: data,
    testid: "".concat(testPrefix, "-row-").concat(idx),
    adminOrMemberActingOnOwner: data.user.effectiveRole() === UserRole.Owner && (userRole == UserRole.Admin || userRole == UserRole.Member),
    isEditingTheSoleOwner: data.user.isOwner() && members.filter(m => m.isOwner()).length === 1,
    isCurrentUser: data.user.id() === userId,
    onChangeRole: onChangeRole,
    onResendInvitation: onResendInvitation,
    onRemoveFromTeam: onRemoveFromTeam
  })))), activeAccountants.length > 0 && /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: accountantsColumnDefs,
    hasActionColumn: true
  }, /*#__PURE__*/React.createElement(Typography, {
    weight: "normal",
    className: cn(styles.tableTitle, styles.secondTableTitle)
  }, "Accountants with access to your business"), /*#__PURE__*/React.createElement(TableList.Header, {
    items: accountantsHeaderItems
  }), /*#__PURE__*/React.createElement("div", {
    "data-testid": testPrefix + '-acc-table-body'
  }, activeAccountants.map((acc, idx) => /*#__PURE__*/React.createElement(TableList.Row, {
    key: acc.id(),
    items: [{
      value: acc.companyName()
    }, {
      value: acc.formattedDate()
    }, {
      value: /*#__PURE__*/React.createElement(Typography, {
        skin: "success"
      }, "Active")
    }],
    actionColumn: /*#__PURE__*/React.createElement(IconButton, {
      skin: "error",
      intent: Intent.RemoveAccountAccess,
      "data-testid": "".concat(testPrefix, "-").concat(idx, "-rm-acc-access"),
      onClick: () => onRemoveAccountAccess(acc)
    }, /*#__PURE__*/React.createElement(TrashLine, null))
  })))));
}
export default function TeamPage() {
  const {
    api
  } = useStoreActions();
  useEffect(() => {
    document.title = title;
  }, []);
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: () => api.member.getAll(),
    renderChild: _ref4 => {
      let {
          data
        } = _ref4,
        rest = _objectWithoutProperties(_ref4, _excluded);
      return /*#__PURE__*/React.createElement(TeamPageWithData, _extends({}, data, rest));
    },
    renderError: _ref5 => {
      let {
        refresh
      } = _ref5;
      return /*#__PURE__*/React.createElement(PageEmptyState, {
        "data-testid": "".concat(testPrefix, "-error-page"),
        illustration: /*#__PURE__*/React.createElement(PageErrorStateIcon, null),
        message: "Something went wrong. Try to refresh the data",
        actions: /*#__PURE__*/React.createElement(Button, {
          onClick: () => refresh(),
          "data-testid": "".concat(testPrefix, "-refresh-btn")
        }, "Refresh")
      });
    }
  });
}